import React, { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

// Form wrapper component
export const Form = ({ children, onSubmit, className = '', ...props }) => (
  <form onSubmit={onSubmit} className={`bg-white rounded-lg p-6 max-w-4xl mx-auto ${className}`} {...props}>
    {children}
  </form>
);

// Interactive tooltip component for info buttons
const InfoTooltip = ({ tooltip, children }) => {
  const [isVisible, setIsVisible] = useState(false);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsVisible(!isVisible);
  };

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  return (
    <div className="relative inline-block">
      <span
        className="ml-1 text-gray-400 cursor-help"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        title={tooltip}
      >
        {children}
      </span>
      {isVisible && (
        <div className="absolute z-50 w-64 p-2 mt-1 text-sm text-white bg-gray-800 rounded-md shadow-lg -top-2 left-6 transform -translate-y-full">
          <div className="relative">
            {tooltip}
            <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          </div>
        </div>
      )}
    </div>
  );
};

// Common form components used across income forms
export const FormField = React.forwardRef(({ label, name, type = 'text', register, error, tooltip, prefix, ...props }, ref) => {
  // Check if register is a function before using it
  const registerField = typeof register === 'function' ? register(name) : {};

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {tooltip && (
          <InfoTooltip tooltip={tooltip}>
            <InformationCircleIcon className="h-4 w-4 inline" />
          </InfoTooltip>
        )}
      </label>
      <div className="relative">
        {prefix && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500 sm:text-sm">{prefix}</span>
          </div>
        )}
        <input
          type={type}
          ref={ref}
          {...registerField}
          className={`block w-full px-3 py-2 ${prefix ? 'pl-7' : ''} bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
            error ? 'border-red-300' : ''
          } ${props.disabled ? 'bg-gray-100' : ''} ${
            type === 'date' ? '[&::-webkit-calendar-picker-indicator]:text-gray-900 [&::-webkit-calendar-picker-indicator]:dark:invert' : ''
          }`}
          {...props}
        />
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error.message}</p>}
    </div>
  );
});

FormField.displayName = 'FormField';

export const FormSelect = React.forwardRef(({ label, name, options, register, error, tooltip, ...props }, ref) => {
  // Check if register is a function before using it
  const registerField = typeof register === 'function' ? register(name) : {};

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {tooltip && (
          <InfoTooltip tooltip={tooltip}>
            <InformationCircleIcon className="h-4 w-4 inline" />
          </InfoTooltip>
        )}
      </label>
      <select
        ref={ref}
        {...registerField}
        className={`block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
          error ? 'border-red-300' : ''
        }`}
        {...props}
      >
        <option value="">Select {label.toLowerCase()}</option>
        {options?.map((option) => (
          <option key={option.id} value={option.id} className="bg-white text-gray-900">
            {option.label}
          </option>
        ))}
      </select>
      {error && <p className="mt-1 text-sm text-red-600">{error.message}</p>}
    </div>
  );
});

FormSelect.displayName = 'FormSelect';

export const FormCheckbox = React.forwardRef(({ label, name, register, error, tooltip, defaultChecked, labelAbove, ...props }, ref) => {
  // Check if register is a function before using it
  const registerField = typeof register === 'function' ? register(name) : {};

  return (
    <div className="mb-6">
      {labelAbove && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {tooltip && (
            <InfoTooltip tooltip={tooltip}>
              <InformationCircleIcon className="h-4 w-4 inline" />
            </InfoTooltip>
          )}
        </label>
      )}
      <div className="flex items-center">
        <input
          type="checkbox"
          ref={ref}
          id={name}
          defaultChecked={defaultChecked}
          {...registerField}
          className={`h-6 w-6 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${props.className || ''}`}
          {...props}
        />
        {!labelAbove && (
          <label htmlFor={name} className="ml-2 block text-sm font-medium text-gray-700">
            {label}
            {tooltip && (
              <InfoTooltip tooltip={tooltip}>
                <InformationCircleIcon className="h-4 w-4 inline" />
              </InfoTooltip>
            )}
          </label>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error.message}</p>}
    </div>
  );
});

FormCheckbox.displayName = 'FormCheckbox';

export const FormSection = ({ title, children, className = '' }) => (
  <div className={`mb-8 ${className}`}>
    <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
    {children}
  </div>
);

export const FormActions = ({ children, className = '' }) => (
  <div className={`flex justify-end space-x-3 mt-8 pt-4 border-t border-gray-200 ${className}`}>
    {children}
  </div>
);

export const FormError = ({ message }) => (
  <div className="rounded-md bg-red-50 p-4 mb-6">
    <div className="flex">
      <div className="flex-shrink-0">
        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="ml-3">
        <p className="text-sm text-red-700">{message}</p>
      </div>
    </div>
  </div>
); 